import { useState, useRef, useEffect } from 'react';

interface UsePullToRefreshOptions {
  enabled: boolean;
  onRefresh: () => void;
  threshold?: number;
}

const usePullToRefresh = ({ enabled, onRefresh, threshold = 50 }: UsePullToRefreshOptions) => {
  const [startY, setStartY] = useState<number>(0);
  const [pulling, setPulling] = useState<boolean>(false);
  const [pulledDistance, setPulledDistance] = useState<number>(0);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      const distance = e.touches[0].clientY - startY;
      if (distance > 0) {
        e.preventDefault();
        setPulling(true);
        setPulledDistance(distance);
      }
    }
  };

  const handleTouchEnd = () => {
    if (pulling) {
      setPulling(false);
      if (pulledDistance > threshold && !refreshing) {
        setRefreshing(true);
        if (indicatorRef.current) {
          indicatorRef.current.textContent = 'Caricamento...';
        }
        try {
          onRefresh();
        } catch (error) {
          console.error('Refresh failed:', error);
        } finally {
          resetRefresh();
        }
      } else {
        resetRefresh();
      }
    }
  };

  const resetRefresh = () => {
    setPulledDistance(0);
    setRefreshing(false);
    if (indicatorRef.current) {
      indicatorRef.current.textContent = 'Scorri per caricare...';
    }
  };

  useEffect(() => {
    if (!pulling) {
      setPulledDistance(0);
    }
  }, [pulling]);

  if (!enabled) {
    return {
      pullToRefreshProps: {},
      indicatorProps: {},
      refreshing: false,
    };
  }

  const pullToRefreshProps = {
    ref: containerRef,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    style: { marginTop: `${pulledDistance}px` },
  };

  const indicatorProps = {
    ref: indicatorRef,
    className: `absolute top-[-40px] left-1/2 transform -translate-x-1/2 w-[150px] text-center p-2 bg-gray-100 rounded-md ${
      pulledDistance > 0 ? 'block' : 'hidden'
    }`,
    children: pulledDistance > threshold ? 'Rilascia per aggiornare' : 'Scorri per aggiornare...',
  };

  return {
    pullToRefreshProps,
    indicatorProps,
    refreshing,
  };
};

export default usePullToRefresh;
